<template>
  <!-- AppCalendar do app + a lista de horários do dia -->
  <div class="calendar text-primary">
    <div class="d-flex align-items-center justify-content-between calendar-header">
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="calendar-nav" :disabled="selectedMonthPosition == 0" aria-label="Mês anterior" @click="handleChangeMonth(false)">
          <span class="fas fa-chevron-circle-left"></span>
        </button>
        <p class="text-subtitle m-0" id="calendar-month">{{ currentMonthString }}</p>
        <button type="button" class="calendar-nav" :disabled="selectedMonthPosition == months.length - 1" aria-label="Próximo mês" @click="handleChangeMonth(true)">
          <span class="fas fa-chevron-circle-right"></span>
        </button>
      </div>
      <button type="button" class="btn btn-link p-0 text-subtitle text-secondary text-decoration-none" id="calendar-today" @click="todayPressed">Hoje</button>
    </div>

    <div class="calendar-grid">
      <span v-for="week in weekDays" :key="week" class="text-label calendar-weekday">{{ week }}</span>
      <template v-for="(week, index) in currentMonth?.month" :key="index">
        <button
          v-for="day in week"
          :key="day.formattedDate"
          type="button"
          class="calendar-day"
          :class="dayClass(day)"
          :disabled="day.disabled && !day.isToday"
          :data-date="day.formattedDate"
          @click="handleDateSelected(day)"
        >
          {{ Number(day.day) }}
        </button>
      </template>
    </div>

    <AppSection class="calendar-times" title="Horários disponíveis">
      <template #icon><TimezoneAlert class="order-last" /></template>
      <LoadingContainer v-if="loadingTimes" />
      <template v-else>
        <AppCheckRow v-if="allowOnline" label="Agendar para agora!" :checked="selectedTime == 'online'" id="time-online" @click="handleTimeSelected('online')">
          <template #extra><span class="app-badge app-badge-success text-label">Disponível agora</span></template>
        </AppCheckRow>
        <AppCheckRow
          v-for="time in times"
          :key="time.date"
          :label="String(time.formattedDate.capitalizeFirst())"
          :checked="selectedTime == time.date"
          class="calendar-time"
          @click="handleTimeSelected(time.date)"
        />
        <p v-if="!times.length" class="text-paragraph text-primary50 text-center calendar-empty">Nenhum horário disponível</p>
      </template>
    </AppSection>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import moment from "moment";
import type { CalendarDay, CalendarMonth } from "@/app/formatters/schedule";
import AppCheckRow from "@/components/app/AppCheckRow.vue";
import AppSection from "@/components/app/AppSection.vue";
import { useAvailableTimes } from "@/composables/useSchedule";

const props = withDefaults(defineProps<{ months?: CalendarMonth[]; psiId?: string; allowOnline?: boolean }>(), {
  months: () => [],
  allowOnline: false,
});
const emit = defineEmits<{ onDateChanged: [date: string] }>();

const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const selectedTime = ref("");
const selectedDate = ref<string>("");
const selectedMonthPosition = ref(0);

const currentMonth = computed(() => props.months[selectedMonthPosition.value]);
const currentMonthString = computed(() =>
  `${(currentMonth.value?.date ?? moment(selectedDate.value || undefined)).format("MMMM [de] YYYY")}`.capitalizeFirst().toString(),
);

const { data: timesData, isFetching: loadingTimes } = useAvailableTimes(() => Number(props.psiId) || undefined, selectedDate);
const times = computed(() => (selectedDate.value ? (timesData.value ?? []) : []));

// trocar o dia limpa o horário escolhido (a não ser "online", que vale para qualquer dia)
watch(selectedDate, () => {
  if (selectedTime.value != "online") {
    selectedTime.value = "";
    emit("onDateChanged", "");
  }
});
watch(selectedTime, (t) => emit("onDateChanged", t));

onMounted(() => {
  if (props.allowOnline) selectedTime.value = "online";
  selectedDate.value = props.months[0]?.firstAvailableDay || "";
});

function handleChangeMonth(plus = true) {
  const pos = selectedMonthPosition.value + (plus ? 1 : -1);
  // são 6 meses (buildMonths); nas pontas não faz nada
  if (pos < 0 || pos > props.months.length - 1) return;
  selectedMonthPosition.value = pos;
  selectedDate.value = props.months[pos].firstAvailableDay || "";
}
// "Hoje": volta para o mês atual e marca o dia de hoje (como no app; sem horários, aparece o aviso)
function todayPressed() {
  selectedMonthPosition.value = 0;
  selectedDate.value = moment().format("YYYY-MM-DD");
}
function handleDateSelected(day: CalendarDay) {
  if (!day.disabled || day.isToday) selectedDate.value = day.formattedDate;
}
function dayClass(day: CalendarDay) {
  if (day.formattedDate == selectedDate.value) return "selected";
  if (day.isToday) return "today";
  if (day.disabled) return "disabled";
  return "";
}
function handleTimeSelected(timeAndDate: string) {
  selectedTime.value = timeAndDate;
  emit("onDateChanged", timeAndDate);
}
</script>
<style scoped lang="scss">
@import "@/assets/theme/scss/user-variables";

.calendar-header {
  padding-bottom: 20px;
}

.calendar-nav {
  border: 0;
  background: transparent;
  padding: 0;
  color: $secondary;
  font-size: 24px;
  line-height: 1;
}

.calendar-nav:disabled {
  color: $primary25;
}

/* 7 colunas, os dias em círculos de 40px como no app */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 10px;
  justify-items: center;
  padding-bottom: 20px;
}

.calendar-weekday {
  font-weight: 600;
  padding-bottom: 6px;
}

.calendar-day {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: $primary;
  font-size: 16px;
  font-weight: 600;
  padding: 0;
}

.calendar-day.today {
  color: $secondary;
}

.calendar-day.disabled {
  color: $primary25;
  font-weight: 400;
  cursor: default;
}

.calendar-day.selected {
  background: $primary;
  color: $white;
}

/* a lista de horários começa na divisória de ponta a ponta (--app-section-gutter de quem usa), como no app */
.calendar-times {
  border-top: 1px solid $primary10;
  border-bottom: 0;
}

.calendar-times :deep(.app-section-title) {
  margin-bottom: 0;
}

.calendar-time:last-of-type {
  border-bottom: 0;
}

.calendar-empty {
  padding: 40px 0;
  margin: 0;
}
</style>
